<script setup lang="ts">
import { backgroundVariants, navVariants, type HeaderProps } from '.';

import { Button } from '@/components/ui/button';
import LinearPilarLogoRaw from '~icons/pilar/linear-logo?raw&width=1em&height=1em';
import messages from './PortalHeader.locale.json';

import { cn } from '@/utils';
import * as typeform from '@typeform/embed';
import { USER_OPTION_SYMBOL, USER_SYMBOL } from '~~/utils/symbols';
import PortalHeaderNav from './PortalHeaderNav.vue';
import { useNavigationStore } from '~/store/navigationStore';
import { useEvents } from '~/composables/events/useEvents';

const userOption = ref<string | undefined>(undefined);

const emit = defineEmits<{
	(e: 'nav-option-being-shown'): void;
	(e: 'nav-option-closed'): void;
}>();

const props = withDefaults(defineProps<HeaderProps>(), {
	isVisible: true,
	transform: 'none',
	variant: 'primary',
	fixed: false,
	links: () => [
		{
			route: '/sobre-a-pilar',
			label: 'Sobre a Pilar'
		}
	],
	contextualHeaderLinks: () => []
});

const {
	loggedIn,
	fetch: fetchSession,
	clear,
	user,
	session
} = useUserSession();

const navigationStore = useNavigationStore();
const { isMenuOpen } = storeToRefs(navigationStore);

const typeformPopup = ref<ReturnType<typeof typeform.createPopup>>();

const { leadBrokerTypeformId } = useRuntimeConfig().public;

const route = useRoute();
const router = useRouter();

onMounted(() => {
	typeformPopup.value = typeform.createPopup(leadBrokerTypeformId, {
		onClose: () => {
			router.replace({
				...route,
				query: { ...route.query, typeform: undefined }
			});
		}
	});
});

watch(route, currentRoute => {
	if (currentRoute.query?.typeform === 'lead-broker') {
		handleOpenTypeform();
	}
});

const handleOpenTypeform = () => {
	typeformPopup.value?.open();
};

const { $mixpanel } = useNuxtApp();
const { t } = useI18n({
	useScope: 'local',
	messages
});
const { trackHeader } = useEvents();

onMounted(async () => {
	if (loggedIn.value) return;

	await fetchSession();
});

provide(USER_SYMBOL, user);
provide(USER_OPTION_SYMBOL, userOption);

watch(userOption, async value => {
	switch (value) {
		case 'logout':
			// Session duration in seconds
			const sessionDuration =
				(new Date().getTime() - new Date(session.value.loggedInAt).getTime()) /
				1000;
			await clear();

			$mixpanel.track('logout_completed', {
				session_duration: sessionDuration
			});

			navigateTo('/');

			break;
		case 'my-data':
			$mixpanel.track('cta_profile_clicked', {
				cta_text: t('my-data'),
				current_page: route.fullPath
			});

			navigateTo('/perfil');

			break;
	}
});

watch(isMenuOpen, isMenuOpen => {
	if (isMenuOpen) {
		emit('nav-option-being-shown');
	} else {
		emit('nav-option-closed');
	}
});

watch(
	() => props.isVisible,
	isVisible => navigationStore.setIsHeaderVisible(isVisible),
	{ immediate: true }
);

const styledLogo = (LinearPilarLogoRaw as unknown as string).replace(
	/<svg/,
	'<svg width="100%" height="100%"'
);
</script>

<template>
	<nav
		class="z-50 flex w-full items-center"
		:class="[
			navVariants({ variant, transform: fixed ? 'none' : transform }),
			{
				'-translate-y-full':
					!fixed && !isVisible && !navigationStore.isMenuMobileOpen
			},
			props.class
		]"
	>
		<section
			class="h-20 w-full items-center justify-between px-4 lg:flex lg:px-12"
		>
			<PortalHeaderNavMobile :variant="variant" class="lg:hidden" />

			<div class="flex size-full items-center gap-x-4 lg:gap-x-11">
				<IconPilarMenu
					class="block size-6 min-h-6 min-w-6 cursor-pointer text-white lg:hidden"
					@click="
						navigationStore.setIsMobileMenuOpen(
							!navigationStore.isMobileMenuOpen
						)
					"
				/>

				<NuxtLink
					to="/"
					aria-label="Página inicial"
					@click="trackHeader.handleEventHeaderClick('Logo', '/')"
				>
					<div class="w-[132px] lg:h-5 lg:w-[164px]" v-html="styledLogo" />
				</NuxtLink>

				<div class="flex size-full items-center justify-between gap-x-6">
					<PortalHeaderNav
						v-model="navigationStore.isNavigationMenuOpen"
						class="hidden lg:block"
					/>
					<div class="ml-auto flex h-full items-center gap-x-6">
						<div class="hidden flex-row items-center gap-4 lg:flex">
							<NuxtLink
								v-for="link in links"
								:key="`header-link: ${link.label}`"
								:to="link.route"
								class="cursor-pointer whitespace-nowrap text-base font-normal leading-[150%] text-white"
								@click="
									trackHeader.handleEventHeaderClick(link.label, link.route)
								"
							>
								{{ link.label }}
							</NuxtLink>
						</div>

						<div
							v-if="loggedIn"
							class="ml-auto flex h-full items-center gap-x-2 lg:ml-0"
						>
							<PortalHeaderNavProfile
								variant="default"
								:bg-variant="variant"
								@open-profile-menu="
									navigationStore.setIsMobileProfileMenuOpen(true)
								"
							/>
							<PortalHeaderNavMobileProfile />
						</div>

						<LoginModal>
							<Button
								v-if="!loggedIn"
								:variant="null"
								class="ml-auto size-min h-[40px] w-[93px] gap-1 border-none p-0 text-white"
								:class="cn(backgroundVariants({ variant }))"
								@click="trackHeader.handleEventHeaderClick('Entrar')"
							>
								<IconMaterialSymbolsLightPersonOutline
									class="size-6 text-beige"
								/>
								<span class="text-sm font-medium leading-[20px]">{{
									t('login')
								}}</span>
							</Button>
						</LoginModal>
					</div>
				</div>
			</div>
		</section>
	</nav>
</template>
